import { i18n } from "shared/i18n";

/**
 * F -- entity kinds as a reader should see them.
 *
 * `ibs_service` is our column value. It reached the screen in three
 * places: the finding page's subject line, the cascade impact card, and
 * the "view this thing" link -- and in two of them a mechanical
 * de-underscore turned it into "ibs service", which reads as a typo
 * rather than as a name.
 *
 * Explicit rather than transformed, for the same reason the workflow
 * catalogue is: no transform knows that `ibs_service` is read
 * "important business service" and `configuration_item` is read
 * "technology asset".
 */
/*
 * Round 9 G -- the words are in the catalogue now; this is the closed
 * vocabulary they belong to. See shared/vocab/workflowState.ts for the
 * reasoning: a hard-coded map here is a map `markUntranslated` never
 * sees, so it cannot translate at any price.
 */
const KIND_KEYS: readonly string[] = [
  "ibs_service",
  "service",
  "capability",
  "vendor",
  "configuration_item",
  "product",
  "channel",
  "journey",
  "connector",
  "outsourcing_contract",
];

export function entityKindLabel(raw: string | null | undefined): string {
  if (!raw) return "";
  const key = raw.trim().toLowerCase();
  if (KIND_KEYS.includes(key)) {
    const out = i18n.t(`vocab.entityKind.${key}`);
    // A key the catalogue has not got back returns the key itself,
    // which would put "vocab.entityKind.service" on a screen.
    if (!out.startsWith("vocab.")) return out;
  }
  // Unrecognised kinds degrade to something readable rather than
  // disappearing -- but they keep their shape, so a missing entry is
  // visible rather than silently plausible.
  const spaced = key.replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Round 16 L2 -- the kind as the register CLASS reads it. An
 * `ibs_service` row of the internal class is a Critical Internal
 * Function, and the cascade card and the constellation headed Treasury
 * settlement "Important business service". Every other kind and class
 * reads as `entityKindLabel` does.
 */
export function entityKindLabelFor(
  raw: string | null | undefined,
  serviceClass: string | null | undefined,
): string {
  const key = (raw ?? "").trim().toLowerCase();
  if ((key === "ibs_service" || key === "ibs") && serviceClass === "internal") {
    return i18n.t("vocab.entityKind.ibs_service_internal");
  }
  return entityKindLabel(key === "ibs" ? "ibs_service" : raw);
}
